// src/pages/TicketTypes/TicketTypeList.jsx
// -----------------------------------------------------------------------------
// Admin screen for Ticket_Type_TBL (and Ticket_Sub_Type_TBL).
// Full CRUD operations built with TableList, TableForm, DeleteConfirmModal,
// and custom hook useTableData.
// -----------------------------------------------------------------------------
import { useState } from 'react';
import { toast } from 'react-toastify';
import ticketTypeService from '../../api/ticketTypeService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';

// Database Schema Config for Ticket_Type_TBL
const TICKET_TYPE_FIELDS = [
  {
    name: 'ticketTypeID',
    label: 'ID',
    type: 'integer',
    isPrimaryKey: true,
    readOnly: true,
  },
  {
    name: 'typeName',
    label: 'Type Name',
    type: 'varchar',
    required: true,
    maxLength: 50,
    placeholder: 'e.g. Bug, Task, Story, Design',
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    defaultValue: 'Active',
    options: [
      { label: 'Active', value: 'Active' },
      { label: 'Inactive', value: 'Inactive' },
    ],
  },
];

export default function TicketTypeList() {
  // 1. Initialize CRUD Hook for Ticket_Type_TBL
  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketTypeService, {
    idKey: 'ticketTypeID',
    itemName: 'Ticket Type',
    initialSort: { key: 'ticketTypeID', direction: 'asc' },
  });

  // 2. State for Sub-Types (Ticket_Sub_Type_TBL)
  const [expandedTypeID, setExpandedTypeID] = useState(null);
  const [subTypes, setSubTypes] = useState([]);
  const [showSubTypeModal, setShowSubTypeModal] = useState(false);
  const [newSubTypeName, setNewSubTypeName] = useState('');
  const [subTypeSubmitting, setSubTypeSubmitting] = useState(false);

  // Toggle open a type row to view/add its sub-types
  const handleToggleExpand = async (ticketTypeID) => {
    if (expandedTypeID === ticketTypeID) {
      setExpandedTypeID(null);
      return;
    }
    setExpandedTypeID(ticketTypeID);
    try {
      const res = await ticketTypeService.getSubTypes(ticketTypeID);
      setSubTypes(res.data || []);
    } catch (err) {
      setSubTypes([]);
    }
  };

  const handleCreateSubType = async (e) => {
    e.preventDefault();
    if (!newSubTypeName.trim()) return;
    setSubTypeSubmitting(true);
    try {
      await ticketTypeService.createSubType(expandedTypeID, { subTypeName: newSubTypeName });
      toast.success('Sub type created successfully.');
      setShowSubTypeModal(false);
      setNewSubTypeName('');
      const res = await ticketTypeService.getSubTypes(expandedTypeID);
      setSubTypes(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create sub type.');
    } finally {
      setSubTypeSubmitting(false);
    }
  };

  const handleDeleteSubType = async (subTypeID) => {
    try {
      await ticketTypeService.removeSubType(subTypeID);
      toast.success('Sub-type deleted successfully.');
      const res = await ticketTypeService.getSubTypes(expandedTypeID);
      setSubTypes(res.data || []);
    } catch (err) {
      toast.error('Failed to delete sub-type.');
    }
  };

  // Columns definition for TableList
  const columns = [
    { key: 'ticketTypeID', label: 'ID', sortable: true, width: '80px' },
    { key: 'typeName', label: 'Type Name', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => <StatusBadge value={row.status} />,
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ticket Types</h1>
          <div className="subtitle">
            Full CRUD management for global ticket types (`Ticket_Type_TBL`) and sub-types.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Ticket Type
        </button>
      </div>

      {/* Main Table List */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="ticketTypeID"
        emptyMessage="No ticket types found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Row Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        renderActions={(row) => {
          const isActive = expandedTypeID === row.ticketTypeID;
          return (
            <button
              className={`btn-table-action btn-table-action-primary ${isActive ? 'active' : ''}`}
              onClick={() => handleToggleExpand(row.ticketTypeID)}
            >
              {isActive ? 'Hide Sub-Types' : 'Sub-Types'}
            </button>
          );
        }}
      />

      {/* Expanded Sub-Types Drawer under table */}
      {expandedTypeID && (() => {
        const parentType = data.find((t) => Number(t.ticketTypeID) === Number(expandedTypeID));
        const parentName = parentType ? parentType.typeName : `Type #${expandedTypeID}`;
        return (
          <div className="card subtype-drawer">
            <div className="flex-header-toolbar">
              <div className="flex-align-gap">
                <h3 className="subtype-title">
                  Sub-Types for <span style={{ color: 'var(--color-primary)' }}>{parentName}</span>
                </h3>
                <span className="subtype-count-badge">
                  {subTypes.length} {subTypes.length === 1 ? 'Sub-Type' : 'Sub-Types'}
                </span>
              </div>
              <button className="btn btn-primary btn-small-padding" onClick={() => setShowSubTypeModal(true)}>
                + Add Sub-Type
              </button>
            </div>

            {subTypes.length === 0 ? (
              <div className="empty-drawer-message">
                No sub-types configured for this ticket type yet. Click &quot;+ Add Sub-Type&quot; to create one.
              </div>
            ) : (
              <div className="flex-wrap-gap">
                {subTypes.map((sub) => (
                  <div
                    key={sub.subTypeID || sub.subTypeName}
                    className="subtype-chip"
                  >
                    <span className="subtype-chip-dot" />
                    <span>{sub.subTypeName}</span>
                    <button
                      type="button"
                      onClick={() => handleDeleteSubType(sub.subTypeID)}
                      title="Delete Sub-Type"
                      className="btn-subtype-delete"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Reusable Form Modal for Create & Edit */}
      {isFormOpen && (
        <TableForm
          fields={TICKET_TYPE_FIELDS}
          initialValues={selectedRecord || { typeName: '', status: 'Active' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Ticket Type"
        />
      )}

      {/* Reusable Confirmation Modal for Delete */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Ticket Type"
        itemLabel={selectedRecord ? selectedRecord.typeName : 'Ticket Type'}
        deleting={deleting}
      />

      {/* Add Sub-Type Modal */}
      {showSubTypeModal && (
        <Modal title="New Sub-Type" onClose={() => setShowSubTypeModal(false)}>
          <form onSubmit={handleCreateSubType}>
            <div className="form-group">
              <label>Sub-Type Name <span className="text-danger">*</span></label>
              <input
                type="text"
                value={newSubTypeName}
                onChange={(e) => setNewSubTypeName(e.target.value)}
                placeholder="e.g. Frontend Bug, UI Polish, High Priority"
                required
              />
            </div>
            <div className="flex-end-gap">
              <button type="button" className="btn btn-secondary" onClick={() => setShowSubTypeModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={subTypeSubmitting}>
                {subTypeSubmitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}